import { Link, NavLink, useNavigate, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../lib/auth";
import { useState, useRef, useEffect } from "react";
import AudiencePicker from "./AudiencePicker";
// EngagementBar removed from header per founder direction; component file kept.
import CatalogFreshnessBadge from "./CatalogFreshnessBadge";
import { DepthToggle } from "./DepthToggle";
import LanguagePicker from "./LanguagePicker";
import IdentityPill from "./IdentityPill";
import { isRouteVisibleFor, useAudience } from "../lib/audience";

// Primary nav — Maldek: clinical reasoning you can see, practice, and measure.
// Three commercial-grade entry points + the institutional CTA.
// "Clinical Reasoning" intentionally removed from the primary nav
// (Feb 15, 2026 — founder direction: it's already present in the
// secondary "Predict" zone of NAV_ZONES, so showing it twice was
// repetitive).
const primaryLinks = [
  { to: "/how-it-works", label: "How It Works" },
  { to: "/", label: "Home", end: true },
  { to: "/ce-hub", label: "CE Hub", studentOnly: true },
  { to: "/for-institutions", label: "Institutions" },
  { to: "/for-schools", label: "Schools" },
  { to: "/analytics", label: "Analytics" },
];

// FOUR GROUPS — the Clinical Reasoning journey (June 2026 reorg,
// founder-approved): Solve → Understand → Teach → Reach.
// Every legacy surface still has a home — see /modules for the full
// directory. The chip strip, mega-menu, homepage curriculum section,
// and footer all tell the same story.
// Darker zone accents for the bright menu sheet (the zone.accent pastels
// are tuned for dark surfaces and wash out on white).
const ZONE_INK = { solve: "#6B8F71", understand: "#6B8F71", teach: "#6B8F71", reach: "#8a6d2e" };

const NAV_ZONES = [
  {
    id: "solve",
    label: "Solve",
    blurb: "Interactive patient encounters and simulations under pressure.",
    accent: "#6B8F71",
    items: [
      { to: "/time-machine",      label: "Time Machine",        short: "Time Machine", sub: "travel", desc: "The Time Machine — diagnose the emergency, then travel 15/10/5 years into the patient's past to find the first physiologic break and change the future." },
      { to: "/patient",           label: "Patient",                                  sub: "case", desc: "Single-patient case engine — work through history, exam, workup, and management as the primary clinician." },
      { to: "/nclex",             label: "NCLEX Quiz",          short: "NCLEX",     sub: "exam", desc: "Adaptive NCLEX-style question bank with explanations, reasoning-pattern tags, framework chips, and cohort tracking." },
      { to: "/tutor",             label: "AI Tutor",                                 sub: "chat", desc: "Multi-turn AI tutor (gpt-5.5) — ask anything about a condition, lab, or medication and get the mechanism, the monitoring, and what to teach." },
    ],
  },
  {
    id: "understand",
    label: "Understand",
    blurb: "Translate physiology, disease, labs, meds, and lived experience.",
    accent: "#6B8F71",
    items: [
      { to: "/clinical-reasoning", label: "Clinical Reasoning",                       sub: "workspace", desc: "Mechanism-first framework + 8-step case walkthrough in one unified workspace — same topic, two camera angles." },
      { to: "/explain",            label: "Patho",                          sub: "deep dive",  desc: "Full pathophysiology page — normal anatomy & physiology, the mechanism, the failure cascade, the Compensation → Decompensation → Failure arc, why every symptom appears, and what each medication fixes." },
      { to: "/symptoms",           label: "Symptoms",                                 sub: "rev · fwd", desc: "Reverse mode: drop in findings → ranked differential. Forward mode: name a diagnosis → red flags + mortality clock." },
      { to: "/lab-reference",      label: "Labs",                                     sub: "decode", desc: "240+ lab values with reference ranges, pediatric variants, and clinical 'why this threshold' rationale. Type a disease to see the panel you'd order." },
      { to: "/imaging-procedures", label: "Imaging & Procedures", short: "Imaging",   sub: "scans", desc: "Plain-English preview of common imaging, screenings, procedures, vaccines, and dental visits by age group." },
      { to: "/med-library",        label: "Medications",         short: "Meds",       sub: "library", desc: "Medication Library — browse by body system and drug class. Mechanism, dosing, interactions, monitoring, and what to teach the patient." },
      { to: "/eastern-workspace",  label: "Therapeutics",        short: "Eastern",    sub: "compare", desc: "Western pharmacotherapy vs Eastern / integrative therapies side-by-side — when each is supported by evidence." },
      { to: "/cooking",            label: "Cultural Cooking",    short: "Cooking",    sub: "food", desc: "Disease-aware cooking — culturally relevant recipes adapted for diabetes, CKD, HTN, and more." },
      { to: "/safety-net",         label: "Resources",                                sub: "help", desc: "Local + national safety-net resources — food, shelter, transportation, prescription assistance, hotlines." },
      { to: "/pediatrics",         label: "Pediatrics",          short: "Peds",       sub: "lifespan", desc: "Pediatric Master Study Map — RN + FNP lifespan. 89 deterministic conditions, red flags, emergency med pairings, parent teaching, NCLEX pediatric." },
    ],
  },
  {
    id: "teach",
    label: "Teach",
    blurb: "Educators & programs — see where the class stops understanding the patient.",
    accent: "#a5673f",
    items: [
      { to: "/analytics",          label: "Faculty Analytics",   short: "Faculty",   sub: "classes", desc: "The master analytics view — reasoning-pipeline breakdown, class comparison, recent attempts, and the pre/post survey shift, all in one dashboard." },
      { to: "/mystery",          label: "Student Pilot",       short: "Pilot",     sub: "students", desc: "The Clinical Mystery — solve interactive patient mysteries; every attempt is scored across the reasoning pipeline." },
      { to: "/for-schools",        label: "For Schools",         short: "Schools",   sub: "programs", desc: "Deans, program directors, simulation coordinators — bring the method to your program." },
      { to: "/for-institutions",   label: "For Institutions",    short: "Institutions", sub: "workforce", desc: "CNOs, nurse educators, clinical directors — workforce reasoning at scale." },
      { to: "/ce-hub",             label: "CE Hub",                                   sub: "clinicians", desc: "Continuing-education surfaces for practicing clinicians." },
    ],
  },
  {
    id: "reach",
    label: "Reach",
    blurb: "Community & workforce — reasoning starts in the neighborhood, years before the ED.",
    accent: "#d9c58a",
    items: [
      { to: "/community",          label: "Community Health",    short: "Community", sub: "track", desc: "The patient-and-family track — plain-English explainers, labs, meds, cooking, and safety-net resources." },
      { to: "/schools",            label: "Maldek Schools",  short: "K-12", sub: "students", desc: "Middle school, high school, and CTE — discover healthcare by experiencing healthcare. One patient, many professions." },
      { to: "/bls-academy",        label: "BLS/ACLS Academy",    short: "BLS/ACLS",  sub: "training", desc: "AHA-guidelines-aligned Basic and Advanced Life Support training — skills rubrics, megacode simulation, and adaptive exam prep." },
      { to: "/workforce",          label: "Workforce",                                sub: "employers", desc: "Employer-facing training pathways for the clinical workforce." },
    ],
  },
];

// Community-specific zoning — the platform restructures the same
// surfaces into two pillars the average patient/family actually
// thinks in: what you need at the doctor's office vs what you do
// once you get home. Household Simulator lives in community-only
// per founder direction (the SDOH/risk story is patient-facing);
// Cognitive Simulator + Reasoning Engine stay clinician/student
// surfaces and are NOT shown to community readers.
const COMMUNITY_ZONES = [
  {
    id: "see-doctor",
    label: "When you see the doctor",
    blurb: "Walk into the appointment prepared.",
    accent: "#6B8F71",
    items: [
      { to: "/walkthrough",         label: "Explain",                                  sub: "short", desc: "Quick condition walkthrough in plain English — what it is, who gets it, what to expect." },
      { to: "/explain-long",        label: "Patho",                          sub: "deep dive",  desc: "The full pathophysiology story in plain English — how the body works when healthy, why it breaks, the stage-by-stage arc from normal to severe, why each symptom appears, and what every medicine actually does." },
      { to: "/symptoms",            label: "Symptoms",                                 sub: "rev · fwd", desc: "Tell us what you're feeling and we'll list possible causes (Reverse) — or look up the watch-list for a diagnosis you've been given (Forward)." },
      { to: "/lab-reference",       label: "Labs",                                     sub: "decode", desc: "Translate any lab on your printout — what it measures, what the numbers mean, and what high/low changes day-to-day." },
      { to: "/med-library",         label: "Medications",         short: "Meds",       sub: "library", desc: "Medication Library — browse by body system and drug class. What each drug does, how to take it, and the side effects worth telling your doctor about." },
      { to: "/imaging-procedures",  label: "Imaging & Procedures", short: "Procedures", sub: "what to expect", desc: "Imaging, screenings, procedures, vaccines, and dental visits — laid out by age group with what each one actually feels like." },
    ],
  },
  {
    id: "at-home",
    label: "When at home",
    blurb: "Care that happens after the appointment.",
    accent: "#6B8F71",
    items: [
      { to: "/eastern-workspace",   label: "Therapeutics",        short: "Eastern",    sub: "compare", desc: "Compare Western and Eastern / integrative therapies side-by-side — when each is supported by evidence." },
      { to: "/cooking",             label: "Cultural Cooking",    short: "Cooking",    sub: "food", desc: "Culturally familiar recipes adapted for diabetes, kidney disease, blood pressure, and more." },
      { to: "/safety-net",          label: "Resources",                                sub: "help", desc: "Local and national safety-net resources — food, shelter, transportation, prescription help, and hotlines." },
      { to: "/household-sim",       label: "Household Sim",       short: "Household",  sub: "SDOH", desc: "Simulate the social factors at home — food, housing, transportation, caregiving — and see how they shift your health risk." },
    ],
  },
];

// Flat lists derived from the zones — these power the existing audience-
// filtering logic and the legacy `visibleReferenceLinks` / `visibleToolLinks`
// consumers without changing their shape. Community uses COMMUNITY_ZONES
// for rendering but the flat list must also include the community-only
// surfaces (Practice Visit) so visibility filtering still picks them up.
const COMMUNITY_EXTRA_ITEMS = [
  { to: "/walkthrough",        label: "Explain",              sub: "short" },
  { to: "/explain-long",       label: "Patho",      sub: "deep dive" },
  { to: "/imaging-procedures", label: "Imaging & Procedures", sub: "what to expect" },
  { to: "/household-sim",      label: "Household Sim",        sub: "SDOH" },
];
const referenceLinks = [
  ...NAV_ZONES.flatMap((z) => z.items),
  ...COMMUNITY_EXTRA_ITEMS,
];
const toolLinks = referenceLinks;

export default function Header() {
  const { user, logout } = useAuth();
  const { audience, founderMode, resetTrack } = useAudience();
  // useAudience() returns the id directly as a string (see lib/audience.jsx),
  // not an object — so use it as-is.
  const audienceId = audience || "student";
  const nav = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const ref = useRef(null);

  // Audience-aware link filtering — community readers don't see clinical-
  // decision surfaces, etc. See `lib/audience.AUDIENCE_ROUTES`.
  // Community sees the literal `Explain · short` label (founder direction
  // Feb 15, 2026 — "put back explain short only for community page").
  function shapeForAudience(links) {
    let filtered = links.filter((l) => isRouteVisibleFor(l.to, audienceId));
    // `/walkthrough` ("Explain · short") is community-only in the menu —
    // student/clinician readers see the same WalkthroughPanel embedded
    // inside `/clinical-reasoning`, so the standalone route is hidden
    // from their secondary nav per founder direction Feb 15, 2026.
    if (audienceId !== "community") {
      filtered = filtered.filter((l) => l.to !== "/walkthrough");
    }
    // For community: top-bar order is locked to —
    //   Labs · Explain · Explain·short · Medications · Street History ·
    //   Therapeutics · Cultural Cooking · (rest)
    // "Explain · short" is the /walkthrough surface — restored per
    // founder direction Feb 15, 2026 ("put back explain short only for
    // community page").
    if (audienceId === "community") {
      const COMMUNITY_ORDER = [
        "/lab-reference",
        "/explain",
        "/walkthrough",     // "Explain · short" for community
        "/med-library",
        "/eastern-workspace",
        "/cooking",
      ];
      const ordered = COMMUNITY_ORDER
        .map((to) => filtered.find((l) => l.to === to))
        .filter(Boolean);
      const rest = filtered.filter((l) => !COMMUNITY_ORDER.includes(l.to));
      return [...ordered, ...rest];
    }
    return filtered;
  }
  const visibleReferenceLinks = shapeForAudience(referenceLinks);
  const visibleToolLinks = shapeForAudience(toolLinks);

  // Close mobile drawer whenever the route changes.
  useEffect(() => {
    setNavOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    function close(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  // Lock body scroll while the mobile menu is open.
  useEffect(() => {
    if (navOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = "hidden";
      return () => {
        document.body.style.overflow = prev;
      };
    }
  }, [navOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!navOpen) return;
    function onKey(e) {
      if (e.key === "Escape") setNavOpen(false);
    }
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  return (
    <header
      className="sticky top-0 z-40 glass border-b border-white/8"
      data-testid="app-header"
    >
      <div className="max-w-[1700px] mx-auto flex items-center gap-2 px-3 sm:px-4 py-3 sm:py-4">
        {/* LEFT — logo. Tagline hidden on mobile so the right-side buttons fit. */}
        <Link
          to="/"
          className="flex items-center gap-2 group min-w-0 mr-1 shrink-0"
          data-testid="logo-link"
        >
          <motion.div
            className="w-9 h-9 rounded-xl overflow-hidden shrink-0"
            style={{
              background: "#2b261c",
              boxShadow: "0 6px 18px rgba(43, 38, 28, 0.35)",
            }}
            whileHover={{ rotate: -8, scale: 1.05 }}
          >
            <img
              src="/brand/icon.png"
              alt="Maldek — Tree of Knowledge"
              className="w-full h-full object-cover"
              data-testid="brand-logo-icon"
            />
          </motion.div>
          <div className="flex flex-col leading-tight min-w-0">
            <span className="text-base sm:text-lg tracking-tight break-words font-semibold" style={{ color: "var(--md-text)", fontFamily: '"Fraunces", Georgia, serif' }}>
              Maldek<span style={{ color: "var(--md-accent)", fontStyle: "italic" }}> by Hill</span>
            </span>
            <span className="hidden 2xl:block text-[10px] uppercase tracking-[0.22em] text-[#8a8271] -mt-0.5 break-words">
              A Hill Clinical Learning System
            </span>
          </div>
        </Link>

        {/* CENTER — desktop nav (primary only). Hidden below xl — with
            13 modules the row gets cramped between 1024-1280px, so those
            widths fall back to the hamburger menu (always available).
            Community readers don't see the Reasoning Engine — it's a
            clinician/student-only surface, per founder direction. */}
        <nav className="hidden xl:flex items-center gap-0 shrink min-w-0 mx-auto" data-testid="primary-nav">
          {primaryLinks
            .filter((l) => !(audienceId === "community" && (l.to === "/clinical-reasoning" || l.to === "/for-schools" || l.to === "/ce-hub" || l.studentOnly)))
            .map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              end={l.end}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-1.5 2xl:px-2.5 py-1.5 rounded-full text-[12px] 2xl:text-[13px] whitespace-nowrap transition-all relative inline-flex items-center gap-1 ${
                  isActive
                    ? "text-white"
                    : l.studentOnly
                      ? "text-[#6B8F71] hover:text-white hover:bg-[#6B8F71]/15"
                      : "text-white/75 hover:text-white hover:bg-white/5"
                }`
              }
              style={({ isActive }) => isActive ? {
                background: "var(--md-primary)",
                boxShadow: "0 6px 18px rgba(44, 90, 140, 0.35)",
                color: "#FFFDFA",
              } : (l.studentOnly ? {
                border: "1px solid rgba(44, 90, 140, 0.35)",
              } : {})}
            >
              {l.label}
              {l.badge && (
                <span
                  className="text-[8px] font-mono uppercase tracking-widest px-1.5 py-0.5 rounded-full"
                  style={{ background: "#6B8F71", color: "#FFFDFA", fontWeight: 700 }}
                >
                  {l.badge}
                </span>
              )}
            </NavLink>
          ))}
        </nav>

        {/* RIGHT — Depth toggle, engagement chip, language, CTA, menu.
            Per founder request, the chips sit on the SAME top row as the
            primary nav. The expanded `max-w-[1700px]` + compact chip
            sizes keep everything visible without overlap. Community is
            locked to plain English so the depth toggle hides there.
            `pl-3 xl:pl-5` puts breathing room between the last nav
            item ("Analytics") and the Depth/Plain-English toggle. */}
        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 pl-3 xl:pl-5" ref={ref}>
          <div className="hidden lg:flex">
            <AudiencePicker variant="chip" />
          </div>
          {audienceId !== "community" && (
            <div className="hidden xl:flex">
              <DepthToggle />
            </div>
          )}
          <LanguagePicker />
          {user ? (
            <div className="relative hidden sm:block">
              <button
                onClick={() => setOpen((o) => !o)}
                data-testid="user-menu-trigger"
                className="flex items-center gap-2 px-2 py-1.5 rounded-full hover:bg-white/5 transition-colors"
              >
                {user.picture ? (
                  <img src={user.picture} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                ) : (
                  <span
                    className="w-7 h-7 rounded-full text-white flex items-center justify-center text-xs font-semibold"
                    style={{ background: "linear-gradient(135deg, #6B8F71 0%, #6B8F71 100%)" }}
                  >
                    {(user.name || user.email)[0].toUpperCase()}
                  </span>
                )}
                <span className="hidden md:block text-sm text-white/90">
                  {user.name?.split(" ")[0] || user.email.split("@")[0]}
                </span>
              </button>
              {open && (
                <div
                  className="absolute right-0 top-12 w-56 glass-card rounded-2xl p-1.5 z-50"
                  data-testid="user-menu"
                >
                  <div className="px-3 py-2 border-b border-white/8">
                    <p className="text-xs text-white/50">signed in as</p>
                    <p className="text-sm text-white break-words">{user.email}</p>
                  </div>
                  <Link
                    to="/dashboard"
                    onClick={() => setOpen(false)}
                    data-testid="menu-dashboard"
                    className="block px-3 py-2 text-sm text-white/85 hover:bg-white/5 rounded-md"
                  >
                    Dashboard
                  </Link>
                  <Link
                    to="/cohorts"
                    onClick={() => setOpen(false)}
                    data-testid="menu-cohorts"
                    className="block px-3 py-2 text-sm text-white/85 hover:bg-white/5 rounded-md"
                  >
                    Cohorts
                  </Link>
                  <button
                    onClick={async () => {
                      await logout();
                      setOpen(false);
                      nav("/");
                    }}
                    data-testid="menu-logout"
                    className="block w-full text-left px-3 py-2 text-sm text-[#6B8F71] hover:bg-white/5 rounded-md"
                  >
                    Sign out
                  </button>
                </div>
              )}
            </div>
          ) : null}

          {/* Primary CTA — only on xl+ so the lg row doesn't clip. */}
          <Link
            to="/for-hospitals"
            data-testid="header-pilot-cta"
            className="hidden xl:inline-flex btn-glow text-sm"
          >
            Request pilot
          </Link>

          {/* Identity pill — signed-in display + sign-out dropdown.
              Renders a "Sign in" CTA when signed-out. Visible on
              lg+ where the nav row has room. */}
          <div className="hidden lg:block">
            <IdentityPill />
          </div>

          <button
            type="button"
            onClick={() => setNavOpen((o) => !o)}
            data-testid="mobile-nav-trigger"
            aria-label={navOpen ? "Close navigation menu" : "Open navigation menu"}
            aria-expanded={navOpen}
            className="inline-flex items-center justify-center gap-1.5 h-10 px-3 rounded-xl btn-ghost active:scale-95"
          >
            {navOpen ? (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            ) : (
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
                <line x1="4" y1="7" x2="20" y2="7" />
                <line x1="4" y1="12" x2="20" y2="12" />
                <line x1="4" y1="17" x2="20" y2="17" />
              </svg>
            )}
            <span className="hidden lg:inline text-xs font-mono uppercase tracking-widest">
              {navOpen ? "Close" : "Menu"}
            </span>
          </button>
        </div>
      </div>

      {/* SECONDARY BAR (lg+) — ONE consolidated row. Zoned nav chips
          flex-grow on the left; track badge + depth toggle + engagement
          live in a compact right cluster. Single row keeps the chrome
          minimal and the page below uncluttered.

          NOTE: We use `overflow-x-auto` (NOT hidden) so the rightmost
          zone (PREDICT) is always reachable even when the chip count
          plus zone labels exceeds the strip's natural width. A custom
          slim scrollbar keeps it visually quiet. */}
      <div
        className="hidden lg:block border-t border-white/10"
        style={{ background: "rgba(250, 246, 238, 0.92)", backdropFilter: "blur(14px) saturate(140%)" }}
        data-testid="reference-bar"
      >
        <div
          className="max-w-[1600px] mx-auto px-3 sm:px-4 py-1.5 flex flex-nowrap items-center gap-x-1 overflow-x-auto slim-scrollbar"
          style={{ justifyContent: "safe center" }}
          data-testid="zoned-ref-strip"
        >
          {(() => {
            const chip = (l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`ref-${l.label.toLowerCase().replace(/[^\w]+/g, "-")}`}
                title={l.sub ? `${l.label} · ${l.sub}` : l.label}
                className={({ isActive }) =>
                  `px-2 py-1 rounded-full whitespace-nowrap transition-all shrink-0 leading-tight flex flex-col items-center ${
                    isActive
                      ? "text-white"
                      : "text-white/90 hover:text-[#6B8F71] hover:bg-white/5"
                  }`
                }
                style={({ isActive }) => isActive ? {
                  background: "linear-gradient(135deg, #6B8F71 0%, #6B8F71 100%)",
                  color: "#2b261c",
                  boxShadow: "0 6px 18px rgba(44, 90, 140, 0.40)",
                } : {}}
              >
                <span className="text-[11px] font-semibold">
                  {(l.short || l.label).replace(/^Reasoning · /, "")}
                </span>
                {l.sub && (
                  <span
                    className="text-[8.5px] font-mono uppercase tracking-[0.16em] leading-tight mt-0.5 opacity-70"
                  >
                    {l.sub}
                  </span>
                )}
              </NavLink>
            );

            if (audienceId === "community") {
              return COMMUNITY_ZONES.map((zone, zi) => {
                const zoneLinks = zone.items.filter((l) =>
                  visibleReferenceLinks.some((vl) => vl.to === l.to)
                );
                if (zoneLinks.length === 0) return null;
                return (
                  <div
                    key={zone.id}
                    className="flex flex-nowrap items-center gap-0.5 px-1 shrink-0"
                    style={zi === 0 ? {} : { borderLeft: `2px solid ${zone.accent}`, paddingLeft: "0.4rem", marginLeft: "0.2rem" }}
                    title={`${zone.label} · ${zone.blurb}`}
                  >
                    {zoneLinks.map(chip)}
                  </div>
                );
              });
            }

            // STUDENT STRIP — the Clinical Reasoning workspace is the
            // centerpiece of the top row (founder direction, June 2026);
            // everything else supports it. Clinical Mystery (staff pilot)
            // + Student Pilot live together off to the right under a
            // visible "Pilots" label (founder direction, June 2026).
            const vis = (l) => visibleReferenceLinks.some((vl) => vl.to === l.to);
            const solveZone = NAV_ZONES.find((z) => z.id === "solve");
            const understandZone = NAV_ZONES.find((z) => z.id === "understand");
            const teachZone = NAV_ZONES.find((z) => z.id === "teach");
            const cr = understandZone.items[0];
            const studentPilotItem = teachZone.items.find((l) => l.to === "/mystery" && l.label === "Student Pilot");
            // Clinical Mystery fully retired from navigation (founder
            // direction, Aug 2026) — the Pilot slot holds the Student Pilot.
            const pilots = [
              studentPilotItem && { ...studentPilotItem, short: "Student Pilot", sub: "students" },
            ].filter((l) => l && vis(l));
            const solve = solveZone.items.filter(vis).filter((l) => l.to !== "/mystery");
            const understand = understandZone.items.slice(1).filter(vis);
            // Founder direction (June 2026 v2): CR pill moved one chip to
            // the right — (…Patho · Symptoms · ‖ CR ‖ · Labs…) — with the
            // blue zone bars wrapped around the CR pill itself.
            const symIdx = understand.findIndex((l) => l.to === "/symptoms");
            const uLeftCount = symIdx >= 0 ? symIdx + 1 : 1;
            const uLeft = understand.slice(0, uLeftCount);
            const uRight = understand.slice(uLeftCount);
            return (
              <>
                {solve.length > 0 && (
                  <div className="flex flex-nowrap items-center gap-0.5 px-1 shrink-0" title="Solve · Interactive patient mysteries and simulations under pressure.">
                    {solve.map(chip)}
                  </div>
                )}
                {uLeft.length > 0 && (
                  <div
                    className="flex flex-nowrap items-center gap-0.5 px-1 shrink-0"
                    title="Understand · Translate physiology, disease, labs, meds, and lived experience."
                  >
                    {uLeft.map(chip)}
                  </div>
                )}
                {vis(cr) && (
                  <div
                    className="flex items-center shrink-0"
                    style={{ borderLeft: "2px solid #6B8F71", borderRight: "2px solid #6B8F71", padding: "0 0.6rem", margin: "0 0.3rem" }}
                    data-testid="ref-cr-bars"
                  >
                    <NavLink
                      key={cr.to}
                      to={cr.to}
                      data-testid="ref-clinical-reasoning"
                      title={`${cr.label} · ${cr.sub} — the centerpiece of the platform`}
                      className="px-4 py-1.5 rounded-full whitespace-nowrap transition-all shrink-0 leading-tight flex flex-col items-center hover:-translate-y-0.5"
                      style={({ isActive }) => isActive ? {
                        background: "linear-gradient(135deg, #6B8F71 0%, #6B8F71 100%)",
                        color: "#2b261c",
                        boxShadow: "0 6px 22px rgba(44, 90, 140, 0.55)",
                      } : {
                        background: "rgba(44, 90, 140, 0.10)",
                        border: "1px solid rgba(44, 90, 140, 0.55)",
                        boxShadow: "0 0 16px rgba(44, 90, 140, 0.28)",
                        color: "#6B8F71",
                      }}
                    >
                      <span className="text-[12px] font-extrabold tracking-tight">Clinical Reasoning</span>
                      <span className="text-[8.5px] font-mono uppercase tracking-[0.18em] leading-tight mt-0.5 opacity-80">
                        workspace · core
                      </span>
                    </NavLink>
                  </div>
                )}
                {uRight.length > 0 && (
                  <div
                    className="flex flex-nowrap items-center gap-0.5 px-1 shrink-0"
                    title="Understand · Translate physiology, disease, labs, meds, and lived experience."
                  >
                    {uRight.map(chip)}
                  </div>
                )}
                {pilots.length > 0 && (
                  <div
                    className="flex flex-nowrap items-center gap-0.5 shrink-0 rounded-full pl-2 pr-1 py-0.5 ml-3"
                    style={{ border: "1px solid rgba(217,197,138,0.45)", background: "rgba(217,197,138,0.07)" }}
                    title="Pilots · Clinical Mystery for staff, Student Pilot for assigned learners."
                    data-testid="ref-pilots-group"
                  >
                    <span
                      className="text-[8.5px] font-mono uppercase tracking-[0.22em] px-1 shrink-0"
                      style={{ color: "#d9c58a" }}
                      data-testid="ref-pilots-label"
                    >
                      Pilot
                    </span>
                    {pilots.map(chip)}
                  </div>
                )}
              </>
            );
          })()}
        </div>
      </div>
      {/* ALL PAGES DROPDOWN — drops down from the header and overlays the
          page content below. Positioned 'fixed' so it stays attached to the
          sticky header even when the page has scrolled. */}
      <AnimatePresence>
        {navOpen && (
          <>
            {/* Click-outside + scroll-lock overlay, beneath the dropdown.
                Starts below the header (top-16 ≈ 64px) so the hamburger stays
                clickable to close. */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
              onClick={() => setNavOpen(false)}
              data-testid="mobile-nav-overlay"
              className="fixed left-0 right-0 bottom-0 top-[68px] z-[55]"
              style={{ background: "rgba(43, 38, 28, 0.35)", backdropFilter: "blur(8px)" }}
              aria-hidden
            />
            {/* BRIGHT HORIZONTAL DROPDOWN — sits flush below the header.
                Light "Clinical Daylight" surface so the menu reads as a
                bright sheet over the page. */}
            <motion.div
              initial={{ y: -8, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: -8, opacity: 0 }}
              transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
              className="fixed left-0 right-0 top-[68px] z-[60] border-b border-[#6B8F71]/30 shadow-2xl"
              style={{
                background: "linear-gradient(180deg, #ffffff 0%, #F5EFE3 100%)",
              }}
              data-testid="mobile-nav-panel"
              role="dialog"
              aria-modal="true"
              aria-label="Site navigation"
            >
              <div className="max-w-7xl mx-auto px-6 py-7">
                {/* PRIMARY ROW — main pathways, horizontal pill cards. */}
                <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71] mb-3">
                  Main pathways
                </p>
                <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-2">
                  {primaryLinks
                    .filter((l) => !(audienceId === "community" && (l.to === "/clinical-reasoning" || l.to === "/for-schools" || l.to === "/ce-hub" || l.studentOnly)))
                    .map((l) => (
                    <NavLink
                      key={l.to}
                      to={l.to}
                      end={l.end}
                      onClick={() => setNavOpen(false)}
                      data-testid={`mobile-nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between gap-2 px-4 py-3 rounded-xl text-sm transition-colors border ${
                          isActive
                            ? "bg-[#6B8F71] border-[#6B8F71] text-white"
                            : "bg-[#6B8F71] border-[#6B8F71] text-white hover:bg-[#6B8F71]"
                        }`
                      }
                    >
                      <span className="font-medium">{l.label}</span>
                      <span className="text-base opacity-50">→</span>
                    </NavLink>
                  ))}
                </div>

                {/* DIVIDER */}
                <div className="my-6 border-t border-[#e3dccb]" />

                {/* TOOL ZONES — 4 column groups laid out horizontally so
                    the whole tool surface is visible at a glance. Each
                    column has a colored zone label so the mental model
                    (Learn → Practice → Reference → Track) shows up here
                    too, not just on the homepage loop. */}
                <div className="flex items-baseline justify-between mb-3">
                  <p className="text-[10px] font-mono uppercase tracking-[0.28em] text-[#6B8F71]">
                    All pages
                  </p>
                  <p className="text-[10px] font-mono text-[#8a8271]">
                    Four zones · one loop
                  </p>
                </div>
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-1.5"
                  data-testid="mobile-nav-zones"
                >
                  {NAV_ZONES.map((zone) => {
                    if (zone.id === "teach" && audienceId === "community") return null;
                    const zoneLinks = zone.items.filter((l) =>
                      visibleToolLinks.some((vl) => vl.to === l.to)
                    );
                    if (zoneLinks.length === 0) return null;
                    return (
                      <div key={zone.id} className="space-y-1">
                        <p
                          className="text-[10px] font-mono uppercase tracking-[0.28em] mb-2"
                          style={{ color: ZONE_INK[zone.id] || "#6B8F71" }}
                        >
                          {zone.label}
                        </p>
                        <p className="text-[10px] text-[#8a8271] italic mb-2 leading-snug">
                          {zone.blurb}
                        </p>
                        {zoneLinks.map((l) => (
                          <NavLink
                            key={l.to}
                            to={l.to}
                            onClick={() => setNavOpen(false)}
                            data-testid={`mobile-nav-${l.label.toLowerCase().replace(/[^\w]+/g, "-")}`}
                            className={({ isActive }) =>
                              `flex items-center justify-between px-2.5 py-1.5 rounded-lg text-[12px] transition-colors ${
                                isActive
                                  ? "bg-[#6B8F71]/15 text-[#2b261c] font-semibold"
                                  : "text-[#3a4149] hover:bg-[#F3ECDD] hover:text-[#2b261c]"
                              }`
                            }
                          >
                            <span className="break-words">
                              {l.label.replace(/^Reasoning · /, "")}
                            </span>
                            <span className="text-[10px] opacity-60 shrink-0 ml-1">→</span>
                          </NavLink>
                        ))}
                      </div>
                    );
                  })}
                </div>

                {/* CTA ROW — pinned at the bottom, also horizontal. */}
                <div className="mt-6 pt-5 border-t border-[#e3dccb] flex flex-wrap items-center gap-3">
                  <Link
                    to="/for-hospitals"
                    onClick={() => setNavOpen(false)}
                    data-testid="mobile-nav-pilot-cta"
                    className="btn-glow text-sm"
                  >
                    Request a Pilot →
                  </Link>
                  <Link
                    to="/modules"
                    onClick={() => setNavOpen(false)}
                    data-testid="mobile-nav-modules"
                    className="px-4 py-2.5 rounded-lg text-sm border border-[#6B8F71]/50 text-[#6B8F71] hover:bg-[#6B8F71]/10 transition-colors"
                  >
                    All modules →
                  </Link>
                  <Link
                    to="/analytics"
                    onClick={() => setNavOpen(false)}
                    data-testid="mobile-nav-results"
                    className="px-4 py-2.5 rounded-lg text-sm border border-[#6B8F71]/50 text-[#6B8F71] hover:bg-[#6B8F71]/10 transition-colors"
                  >
                    Analytics dashboard
                  </Link>
                  {user && (
                    <>
                      <Link
                        to="/dashboard"
                        onClick={() => setNavOpen(false)}
                        data-testid="mobile-nav-dashboard"
                        className="px-4 py-2.5 text-sm text-[#5d5748] hover:text-[#2b261c]"
                      >
                        Dashboard
                      </Link>
                      <button
                        onClick={async () => {
                          await logout();
                          setNavOpen(false);
                          nav("/");
                        }}
                        data-testid="mobile-nav-logout"
                        className="ml-auto px-4 py-2.5 text-sm text-[#D96C5B] hover:bg-white/[0.04] rounded-lg"
                      >
                        Sign out
                      </button>
                    </>
                  )}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </header>
  );
}
